import type { TransformedProduct } from "../../types/productApiTypes";
import type { ImageSourceType } from "../../../../../types/globalTypes";
import type { RawImage } from "../../../../../types/api/wcStoreApi/wcStoreApiProductsEndpointTypes";

/**
 * Extracts images for a given source type from the product data.
 */
const getImagesBySource = (
  product: TransformedProduct,
  selectedVariation: TransformedProduct | null | undefined,
  source: ImageSourceType,
): RawImage[] => {
  switch (source) {
    case "PARENT_MAIN":
      return product.mainImage ? [product.mainImage] : [];
    case "PARENT_GALLERY":
      return product.galleryImages || [];
    case "VARIATION_MAIN":
      // 1. If a variation is explicitly selected by the user, use its main image
      if (selectedVariation?.mainImage) {
        return [selectedVariation.mainImage];
      }
      // 2. Fallback: If no variation selected but product has a default variation, use its image
      if (product.hasDefaultVariation && product.defaultVariationData?.image) {
        return [product.defaultVariationData.image];
      }
      return [];
    case "VARIATION_GALLERY":
      // If a variation is explicitly selected, use its gallery
      if (
        selectedVariation?.galleryImages &&
        selectedVariation.galleryImages.length > 0
      ) {
        return selectedVariation.galleryImages;
      }
      return [];
    default:
      return [];
  }
};

/**
 * Resolves the primary and hover images for a Product Card.
 */
export const resolveCardImages = (
  product: TransformedProduct | null | undefined,
  config: { primary: ImageSourceType[]; hover: ImageSourceType[] },
): { primaryImage: RawImage | null; hoverImage: RawImage | null } => {
  // Failsafe: Return nulls if product is missing
  if (!product) return { primaryImage: null, hoverImage: null };

  let primaryImage: RawImage | null = null;
  let hoverImage: RawImage | null = null;

  // 1. Resolve Primary Image
  for (const source of config.primary) {
    const images = getImagesBySource(product, null, source);
    if (images.length > 0) {
      primaryImage = images[0];
      break;
    }
  }

  // 2. Resolve Hover Image
  for (const source of config.hover) {
    const images = getImagesBySource(product, null, source);
    // We want the hover image to ideally be different from the primary image
    const candidate = images.find((img) => img.id !== primaryImage?.id);
    if (candidate) {
      hoverImage = candidate;
      break;
    }
  }

  return { primaryImage, hoverImage };
};

/**
 * Resolves, flattens, and deduplicates the image array for the Single Product Gallery.
 */
export const resolveGalleryImages = (
  product: TransformedProduct | null | undefined,
  selectedVariation: TransformedProduct | null | undefined,
  sequence: ImageSourceType[],
): RawImage[] => {
  // Failsafe: Return empty array if product is missing
  if (!product) return [];

  const finalImages: RawImage[] = [];
  const seenIds = new Set<number>();

  for (const source of sequence) {
    const images = getImagesBySource(product, selectedVariation, source);
    for (const img of images) {
      if (!seenIds.has(img.id)) {
        finalImages.push(img);
        seenIds.add(img.id);
      }
    }
  }

  // Safe fallback if absolutely no images matched the rules
  if (finalImages.length === 0 && product.mainImage) {
    finalImages.push(product.mainImage);
  }

  return finalImages;
};
